import dayjs from 'dayjs';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { StateCreator } from 'zustand';
import { firestore } from '../../settings/firebase';

import { Consultation } from 'pages/Admin/Consultations/types';
export interface ConsultationsSlice {
  consultations: Consultation[];
  status: 'init' | 'loading' | 'success' | 'error';
  adminSubscribe: () => {
    unSubscribe: () => void;
  };
  subscribe: () => {
    unSubscribe: () => void;
  };
  add: (item: Consultation) => Promise<{ ok: boolean } | { error: string }>;
  edit: (item: Consultation) => Promise<{ ok: boolean } | { error: string }>;
  delete: (id: string) => Promise<{ ok: boolean } | { error: string }>;
}

const toDateValue = (value: string | number | undefined) => {
  if (value === undefined) {
    return Number.NaN;
  }
  const parsed = dayjs(value as any).valueOf();
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

export const createConsultationsSlice: StateCreator<
  ConsultationsSlice,
  [],
  [],
  ConsultationsSlice
> = setState => ({
  consultations: [],
  status: 'init',

  adminSubscribe: () => {
    setState({ status: 'loading' });

    const unSubscribe = onSnapshot(collection(firestore, 'consultations'), snapshot => {
      const currentConsultations: Consultation[] = [];

      snapshot.forEach(docSnapshot => {
        const data = docSnapshot.data() as Consultation;
        currentConsultations.push({
          ...data,
          id: docSnapshot.id,
        });
      });

      currentConsultations.sort((a, b) => {
        const aDate = toDateValue(a.startDate);
        const bDate = toDateValue(b.startDate);

        if (Number.isNaN(aDate) && Number.isNaN(bDate)) {
          return 0;
        }
        if (Number.isNaN(aDate)) {
          return 1;
        }
        if (Number.isNaN(bDate)) {
          return -1;
        }

        return bDate - aDate;
      });

      setState({
        status: 'success',
        consultations: currentConsultations,
      });
    });

    return { unSubscribe };
  },

  subscribe: () => {
    setState({ status: 'loading' });
    const now = dayjs().utc().toISOString();

    const q = query(
      collection(firestore, 'consultations'),
      where('isPublished', '==', true),
      where('startDate', '<=', now)
    );

    const unSubscribe = onSnapshot(q, snapshot => {
      const currentConsultations: Consultation[] = [];

      snapshot.forEach(docSnapshot => {
        const data = docSnapshot.data() as Consultation;

        currentConsultations.push({
          ...data,
          id: docSnapshot.id,
        });
      });

      currentConsultations.sort((a, b) => {
        const aDate = toDateValue(a.startDate);
        const bDate = toDateValue(b.startDate);

        if (Number.isNaN(aDate) && Number.isNaN(bDate)) {
          return 0;
        }
        if (Number.isNaN(aDate)) {
          return 1;
        }
        if (Number.isNaN(bDate)) {
          return -1;
        }

        return bDate - aDate;
      });

      setState({
        status: 'success',
        consultations: currentConsultations,
      });
    });

    return { unSubscribe };
  },

  add: async item => {
    try {
      const { id: _id, ...payload } = item;
      await addDoc(collection(firestore, 'consultations'), payload);
      return { ok: true };
    } catch (error: any) {
      return { error: 'There was an error creating consultation: ' + error.code };
    }
  },

  edit: async item => {
    if (!item.id) {
      return { error: 'Consultation id is required for edit.' };
    }

    try {
      const { id, ...payload } = item;
      await updateDoc(doc(firestore, 'consultations', id), payload as any);
      return { ok: true };
    } catch (error: any) {
      return { error: 'There was an error updating consultation: ' + error.code };
    }
  },

  delete: async id => {
    try {
      await deleteDoc(doc(firestore, 'consultations', id));
      return { ok: true };
    } catch (error: any) {
      return { error: 'There was an error deleting consultation: ' + error.code };
    }
  },
});

export default createConsultationsSlice;
